import { ReadTaskSummaryData } from '@common/config/api/client'
import { SIZING } from '@common/theme/sizing'
import { useMemo, useImperativeHandle, forwardRef } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'
import Grid from '@mui/material/Grid2'
import { useThemeContext } from '@common/theme'
import { darkTheme } from '@common/theme/surveyJSCustomDarkTheme'
import applyCustomClass from '@common/theme/surveyJSCustomDarkTheme'
import 'survey-core/defaultV2.min.css'

interface EcrfSurveyProps {
  selectedItem: string
  timepoints: ReadTaskSummaryData['timepoints']
  read_task_summary_metadata: ReadTaskSummaryData['read_task_summary_metadata']
  initialAnswers: any // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface EcrfSurveyRef {
  getSurveyData: () => Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
}

const EcrfSurvey = forwardRef<EcrfSurveyRef, EcrfSurveyProps>(function EcrfSurvey(props, ref) {
  const { selectedItem, timepoints, read_task_summary_metadata, initialAnswers } = props
  const { themeMode } = useThemeContext()

  const survey = useMemo(() => {
    applyCustomClass(themeMode)

    let model: Model

    if (selectedItem === 'ecrf') {
      model = new Model(read_task_summary_metadata.ecrf_definition)
      model.data = initialAnswers ? initialAnswers : {}
      model.showCompleteButton = false
    } else {
      const selectedTimepointIndex = Number(selectedItem.replace('timepoint_', ''))
      const timepoint = timepoints[selectedTimepointIndex]

      // Process the timepoint eform definition to ensure it's in the correct format
      let formDefinition
      try {
        if (timepoint.timepoint_eform_definition) {
          if (typeof timepoint.timepoint_eform_definition === 'string') {
            formDefinition = JSON.parse(timepoint.timepoint_eform_definition)
          } else {
            // If it's already an object, use it directly
            formDefinition = {
              elements: Array.isArray(timepoint.timepoint_eform_definition)
                ? timepoint.timepoint_eform_definition
                : [timepoint.timepoint_eform_definition],
            }
          }
        } else {
          formDefinition = { elements: [] }
        }
      } catch (error) {
        console.error('Error processing timepoint eform definition:', error)
        formDefinition = { elements: [] }
      }

      model = new Model(formDefinition)

      // Process submission data
      if (timepoint.timepoint_eform_submission) {
        try {
          const submissionData =
            typeof timepoint.timepoint_eform_submission === 'string'
              ? JSON.parse(timepoint.timepoint_eform_submission)
              : timepoint.timepoint_eform_submission
          model.data = submissionData
        } catch (error) {
          console.error('Error processing timepoint eform submission:', error)
        }
      }

      model.mode = 'display'
    }

    // Apply dark theme if needed
    if (themeMode === 'dark') {
      model.applyTheme(darkTheme)
    }

    return model
  }, [themeMode, selectedItem, read_task_summary_metadata.ecrf_definition, initialAnswers, timepoints])

  useImperativeHandle(
    ref,
    () => ({
      getSurveyData: () => survey.data,
    }),
    [survey],
  )

  return (
    <Grid
      size={SIZING.gridSizeLg}
      height={SIZING.gridHeight}
      overflow="auto"
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none', // Hides the scrollbar for WebKit-based browsers
        },
        scrollbarWidth: 'none', // Hides the scrollbar for Firefox
        width: '100%',
      }}
    >
      <Survey model={survey} />
    </Grid>
  )
})

export default EcrfSurvey
